import React, { useEffect, useRef, useState } from 'react';

export type LazyBlueprintPageProps = {
	pageNumber: number;
	forceVisible?: boolean;
	children: React.ReactNode;
};

const SPINNER_KEYFRAMES = `
@keyframes bp-spin {
	to { transform: rotate(360deg); }
}
`;

const SpinnerPlaceholder = (): React.ReactElement => (
	<div style={{
		width: '1100px',
		height: '850px',
		flexShrink: 0,
		margin: '0 auto 20px auto',
		backgroundColor: '#f5f5f5',
		boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	}}>
		<style>{SPINNER_KEYFRAMES}</style>
		<div style={{
			width: '32px',
			height: '32px',
			border: '3px solid #ddd',
			borderTopColor: '#888',
			borderRadius: '50%',
			animation: 'bp-spin 0.8s linear infinite'
		}} />
	</div>
);

/**
 * One-way lazy mount for blueprint pages. Children mount when the page
 * approaches the viewport and stay mounted permanently (so contentEditable
 * edits are never lost). Shows a spinner placeholder until mounted.
 *
 * Typing performance is handled separately by React.memo on the container
 * and memoized context values — not by unmounting pages.
 */
export const LazyBlueprintPage = (props: LazyBlueprintPageProps): React.ReactElement => {
	const { pageNumber, forceVisible, children } = props;
	const ref = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(pageNumber <= 2);

	useEffect(() => {
		const el = ref.current;
		if (!el || isVisible) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '800px 0px' }
		);

		observer.observe(el);
		// hidden tabs pause the rendering pipeline that delivers IO callbacks — poll proximity so background/scripted scrolling still mounts pages
		const hiddenPoll = setInterval(() => {
			if (!document.hidden) return;
			const rect = el.getBoundingClientRect();
			if (rect.top < window.innerHeight + 800 && rect.bottom > -800) setIsVisible(true);
		}, 400);
		return () => {
			observer.disconnect();
			clearInterval(hiddenPoll);
		};
	}, [isVisible]);

	return (
		<div ref={ref}>
			{(isVisible || forceVisible) ? children : <SpinnerPlaceholder />}
		</div>
	);
};
